import type { Bi } from './sectors'

/**
 * Single SEO source: per-route titles, descriptions, breadcrumbs and schema type,
 * in French and English. Consumed by components/Seo.tsx (head tags + JSON-LD)
 * and components/RelatedLinks.tsx (visible breadcrumb + related capabilities).
 *
 * URL strategy: one path per page. French is the default (no parameter);
 * English is served at the same path with `?lang=en`. Both are self-canonical
 * and linked with hreflang fr-CA / en-CA; x-default points to French.
 */

export const SITE_URL = 'https://bciti.ai'
export const OG_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/a85901d8-a3a9-4f08-9d6e-2ad92d9d221c.jpg'
export const LOGO_BCITI = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png'
export const LOGO_ATNA = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png'

/* Only facts already published on the site. Contact details and social
   profiles are intentionally omitted until BCITI supplies them. */
export const organization = {
  legalName: 'BCITI Inc.',
  name: 'BCITI | ATNA',
  alternateName: ['BCITI', 'ATNA'],
  description: {
    fr: 'BCITI | ATNA relie l’identité, les systèmes, les données et l’intelligence des organisations autour d’un même contexte de confiance pour simplifier les opérations et dégager plus de capacité.',
    en: 'BCITI | ATNA connects an organization’s identity, systems, data and intelligence around one trusted context to simplify operations and create more capacity.',
  } as Bi,
}

export type SchemaKind = 'home' | 'platform' | 'service' | 'intelligence' | 'page' | 'caseStudy' | 'contact'

export type SeoRoute = {
  path: string
  /** Short name used in breadcrumbs and related links */
  name: Bi
  title: Bi
  description: Bi
  kind: SchemaKind
  /** Breadcrumb parent path (omit for top-level pages) */
  parent?: string
  /** Related pages shown above the footer (paths) */
  related?: string[]
  /** Product category for Service schema */
  serviceType?: Bi
}

export const seoRoutes: SeoRoute[] = [
  {
    path: '/',
    name: { fr: 'Accueil', en: 'Home' },
    title: { fr: 'Plateforme numérique et IA pour les municipalités | BCITI | ATNA', en: 'Digital platform & AI for municipalities | BCITI | ATNA' },
    description: {
      fr: 'Portail citoyen, application mobile, requêtes, communications, permis et loisirs sur une seule plateforme, avec une IA qui vérifie avant d’agir. En production depuis 2017.',
      en: 'Resident portal, mobile app, requests, communications, permits and recreation on one platform, with AI that verifies before it acts. In production since 2017.',
    },
    kind: 'home',
  },
  {
    path: '/platform',
    name: { fr: 'Plateforme', en: 'Platform' },
    title: { fr: 'Plateforme de services numériques municipaux | BCITI | ATNA', en: 'Municipal digital services platform | BCITI | ATNA' },
    description: {
      fr: 'Une plateforme qui relie identité, portail citoyen, services municipaux, données et systèmes existants, avec l’IA ATNA intégrée. Questions fréquentes et modules.',
      en: 'One platform connecting identity, resident portal, municipal services, data and existing systems, with ATNA AI built in. FAQ and modules.',
    },
    kind: 'platform',
  },
  {
    path: '/core-infrastructure',
    name: { fr: 'Identité', en: 'Identity' },
    title: { fr: 'Identité numérique citoyenne et authentification unique (SSO) | BCITI', en: 'Resident digital identity & single sign-on (SSO) | BCITI' },
    description: {
      fr: 'Une identité vérifiée et un profil unique pour tous les services de votre municipalité ou organisation : connexion unique, consentements, conformité à la Loi 25.',
      en: 'A verified identity and single profile for every service your municipality or organization offers: single sign-on, consent management, Law 25 compliance.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/digital-card', '/portail', '/atna'],
    serviceType: { fr: 'Identité numérique', en: 'Digital identity' },
  },
  {
    path: '/digital-card',
    name: { fr: 'Carte numérique BCITI', en: 'BCITI Digital Card' },
    title: { fr: 'Carte citoyenne numérique : Carte numérique BCITI', en: 'Digital resident card: BCITI Digital Card' },
    description: {
      fr: 'La Carte numérique BCITI remplace la carte citoyenne en plastique : identité vérifiée, accès aux loisirs, à la bibliothèque et aux services, dans le téléphone.',
      en: 'The BCITI Digital Card replaces the plastic resident card: verified identity and access to recreation, library and municipal services, on the phone.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/core-infrastructure', '/portail', '/licences-animaux'],
    serviceType: { fr: 'Carte d’identité numérique', en: 'Digital identity card' },
  },
  {
    path: '/portail',
    name: { fr: 'Expérience numérique', en: 'Digital experience' },
    title: { fr: 'Portail citoyen, site web et application mobile municipale | BCITI', en: 'Resident portal, website & municipal mobile app | BCITI' },
    description: {
      fr: 'Un site web, un portail citoyen « Mon espace » et une application mobile municipale reliés à la même identité, avec l’assistant ATNA intégré au parcours.',
      en: 'A website, a “My space” resident portal and a municipal mobile app connected to the same identity, with the ATNA assistant built into the journey.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/core-infrastructure', '/digital-card', '/requests', '/atna'],
    serviceType: { fr: 'Expérience numérique', en: 'Digital experience' },
  },
  {
    path: '/requests',
    name: { fr: 'Requêtes ATNA', en: 'ATNA Requests' },
    title: { fr: 'Logiciel de gestion des requêtes citoyennes | Requêtes ATNA', en: '311 & resident request management software | ATNA Requests' },
    description: {
      fr: 'Recevoir les demandes des citoyens par tous les canaux, les qualifier, les affecter aux équipes terrain et informer le citoyen jusqu’à la résolution. En production à la Ville de Richelieu.',
      en: 'Receive resident requests from every channel, qualify them, assign field teams and keep residents informed until resolution. Live at the City of Richelieu.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/communications', '/atna', '/portail'],
    serviceType: { fr: 'Gestion des requêtes', en: 'Request management' },
  },
  {
    path: '/communications',
    name: { fr: 'Communications ATNA', en: 'ATNA Communications' },
    title: { fr: 'Système d’avis et d’alertes aux citoyens | Communications ATNA', en: 'Resident alerts & mass notification system | ATNA Communications' },
    description: {
      fr: 'Alertes d’urgence, travaux, fermetures et collectes : joindre les bons citoyens par texto, appel, courriel ou notification, et mesurer qui a été joint.',
      en: 'Emergency alerts, roadwork, closures and collection updates: reach the right residents by text, voice, email or push, and measure who was reached.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/requests', '/core-infrastructure', '/atna'],
    serviceType: { fr: 'Communications multicanales', en: 'Multichannel communications' },
  },
  {
    path: '/collect',
    name: { fr: 'Collectes ATNA', en: 'ATNA Waste Collection' },
    title: { fr: 'Calendrier et application des collectes municipales | Collectes ATNA', en: 'Waste collection calendar & app for municipalities | ATNA Waste Collection' },
    description: {
      fr: 'Calendrier des collectes par adresse, rappels, collectes spéciales, écocentres et signalements dans une application reliée à chaque citoyen.',
      en: 'Collection calendar by address, reminders, special pickups, eco-centres and missed-collection reports in one app connected to each resident.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/communications', '/requests', '/atna'],
    serviceType: { fr: 'Collectes des matières résiduelles', en: 'Waste collection' },
  },
  {
    path: '/permis',
    name: { fr: 'Permis ATNA', en: 'ATNA Permits' },
    title: { fr: 'Logiciel de permis municipaux en ligne | Permis ATNA', en: 'Online municipal permitting software | ATNA Permits' },
    description: {
      fr: 'Demandes de permis en ligne : vérifier l’admissibilité selon le règlement de zonage, préparer un dossier complet et suivre chaque permis jusqu’à la délivrance.',
      en: 'Online permit applications: check eligibility against zoning by-laws, prepare a complete file and track every permit through to issuance.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/core-infrastructure', '/portail', '/atna'],
    serviceType: { fr: 'Gestion des permis', en: 'Permit management' },
  },
  {
    path: '/loisirs',
    name: { fr: 'Loisirs ATNA', en: 'ATNA Recreation' },
    title: { fr: 'Logiciel d’inscription aux loisirs municipaux | Loisirs ATNA', en: 'Municipal recreation registration software | ATNA Recreation' },
    description: {
      fr: 'Inscriptions aux activités et camps de jour, réservation de plateaux, paiements et gestion des participants dans une seule plateforme municipale.',
      en: 'Registration for programs and day camps, facility booking, payments and participant management in one municipal platform.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/core-infrastructure', '/digital-card', '/communications', '/atna'],
    serviceType: { fr: 'Gestion des loisirs', en: 'Recreation management' },
  },
  {
    path: '/licences-animaux',
    name: { fr: 'Médailles et licences pour animaux ATNA', en: 'ATNA Pet Tags & Licences' },
    title: { fr: 'Médailles et licences pour animaux en ligne | BCITI | ATNA', en: 'Online pet licences & tags for municipalities | BCITI | ATNA' },
    description: {
      fr: 'Enregistrer les chiens et chats, émettre et renouveler les médailles et licences en ligne et relier chaque animal à son propriétaire vérifié.',
      en: 'Register dogs and cats, issue and renew pet tags and licences online and connect each animal to its verified owner.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/core-infrastructure', '/digital-card', '/communications', '/atna'],
    serviceType: { fr: 'Licences pour animaux', en: 'Animal licensing' },
  },
  {
    path: '/smart-water',
    name: { fr: 'Compteurs d’eau ATNA', en: 'ATNA Smart Water' },
    title: { fr: 'Compteurs d’eau intelligents et détection des fuites | Compteurs d’eau ATNA', en: 'Smart water meters & leak detection | ATNA Smart Water' },
    description: {
      fr: 'Des données des compteurs d’eau à l’action : détecter les fuites, aviser le propriétaire et suivre la réparation. En production à Casselman.',
      en: 'From water meter data to action: detect leaks, notify the property owner and track the repair. In production in the Municipality of Casselman.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/core-infrastructure', '/communications', '/atna'],
    serviceType: { fr: 'Gestion des compteurs d’eau', en: 'Water meter management' },
  },
  {
    path: '/analytics',
    name: { fr: 'Analytique', en: 'Analytics' },
    title: { fr: 'Analytique opérationnelle | BCITI | ATNA', en: 'Operational analytics | BCITI | ATNA' },
    description: {
      fr: 'Tableaux de bord et indicateurs opérationnels alimentés par le contexte partagé de la plateforme BCITI | ATNA.',
      en: 'Operational dashboards and indicators powered by the shared context of the BCITI | ATNA platform.',
    },
    kind: 'service',
    parent: '/platform',
    related: ['/atna', '/requests'],
    serviceType: { fr: 'Analytique', en: 'Analytics' },
  },
  {
    path: '/atna',
    name: { fr: 'ATNA', en: 'ATNA' },
    title: { fr: 'ATNA : intelligence artificielle pour les municipalités et organisations | BCITI', en: 'ATNA: AI for municipalities and public organizations | BCITI' },
    description: {
      fr: 'ATNA comprend les demandes, vérifie l’information dans vos sources officielles, recommande et agit avec supervision humaine. Une IA conçue pour le secteur public.',
      en: 'ATNA understands requests, verifies information against your official sources, recommends and acts with human oversight. AI designed for the public sector.',
    },
    kind: 'intelligence',
    parent: '/platform',
    related: ['/platform', '/requests', '/communications', '/trust'],
  },
  {
    path: '/trust',
    name: { fr: 'Confiance et gouvernance', en: 'Trust & governance' },
    title: { fr: 'IA responsable, Loi 25 et sécurité des données | BCITI | ATNA', en: 'Responsible AI, privacy (Law 25) & data security | BCITI | ATNA' },
    description: {
      fr: 'Comment ATNA vérifie avant d’agir : identité vérifiée, contexte autorisé, sources fiables, supervision humaine, traçabilité et données hébergées au Canada.',
      en: 'How ATNA verifies before acting: verified identity, authorized context, trusted sources, human oversight, traceability and data hosted in Canada.',
    },
    kind: 'page',
    related: ['/atna', '/platform'],
  },
  {
    path: '/secteurs',
    name: { fr: 'Secteurs', en: 'Sectors' },
    title: { fr: 'Secteurs — Villes, organisations et écosystèmes connectés | BCITI | ATNA', en: 'Sectors — Cities, organizations & connected ecosystems | BCITI | ATNA' },
    description: {
      fr: 'BCITI | ATNA sert les organisations qui gèrent des écosystèmes complexes de personnes, de relations et de services.',
      en: 'BCITI | ATNA serves organizations that manage complex ecosystems of people, relationships and services.',
    },
    kind: 'page',
    related: ['/platform', '/brossard'],
  },
  {
    path: '/about',
    name: { fr: 'Notre histoire', en: 'Our story' },
    title: { fr: 'Notre histoire | BCITI | ATNA', en: 'Our story | BCITI | ATNA' },
    description: {
      fr: 'D’une file d’attente à Brossard à ATNA : pourquoi BCITI | ATNA pense que la technologie doit réduire la complexité, pas l’accroître.',
      en: 'From a lineup in Brossard to ATNA: why BCITI | ATNA believes technology must remove complexity, not add to it.',
    },
    kind: 'page',
  },
  {
    path: '/privacy-policy',
    name: { fr: 'Politique de confidentialité', en: 'Privacy Policy' },
    title: { fr: 'Politique de confidentialité et témoins | BCITI | ATNA', en: 'Privacy Policy & Cookie Usage Policy | BCITI | ATNA' },
    description: {
      fr: 'Comment Solutions B-CITI inc. recueille, utilise, partage et protège vos renseignements personnels, et comment nous utilisons les témoins (cookies).',
      en: 'How Solutions B-CITI Inc. collects, uses, shares and protects your personal information, and how we use cookies and analytics tools.',
    },
    kind: 'page',
  },
  {
    path: '/brossard',
    name: { fr: 'Étude de cas — Ville de Brossard', en: 'Case study — City of Brossard' },
    title: { fr: 'Étude de cas : Ville de Brossard | BCITI | ATNA', en: 'Case study: City of Brossard | BCITI | ATNA' },
    description: {
      fr: 'Depuis 2017, Brossard relie identité, communications et services avec BCITI | ATNA : 88 266 usagers actifs et 78 % de la population couverte.',
      en: 'Since 2017, Brossard has connected identity, communications and services with BCITI | ATNA: 88,266 active users and 78% population coverage.',
    },
    kind: 'caseStudy',
    related: ['/digital-card', '/communications', '/platform'],
  },
  {
    path: '/casselman',
    name: { fr: 'Municipalité de Casselman — Compteurs d’eau', en: 'Municipality of Casselman — Water meters' },
    title: { fr: 'Municipalité de Casselman : compteurs d’eau intelligents en production | BCITI | ATNA', en: 'Municipality of Casselman: smart water meters in production | BCITI | ATNA' },
    description: {
      fr: 'Compteurs d’eau ATNA déployés dans la Municipalité de Casselman, en Ontario.',
      en: 'ATNA Smart Water deployed in the Municipality of Casselman, Ontario.',
    },
    kind: 'caseStudy',
    related: ['/smart-water', '/communications'],
  },
  {
    path: '/industries/cities-government',
    name: { fr: 'Villes et gouvernements', en: 'Cities & government' },
    title: { fr: 'Plateforme numérique pour villes et municipalités | BCITI | ATNA', en: 'Digital platform for cities & municipalities | BCITI | ATNA' },
    description: {
      fr: 'Identité citoyenne, portail, requêtes, communications, permis, loisirs et compteurs d’eau sur une même plateforme municipale, avec l’IA ATNA.',
      en: 'Resident identity, portal, requests, communications, permits, recreation and water meters on one municipal platform, with ATNA AI.',
    },
    kind: 'page',
    parent: '/secteurs',
    related: ['/platform', '/brossard', '/requests'],
  },
  {
    path: '/industries/airports',
    name: { fr: 'Aéroports et transport', en: 'Airports & transportation' },
    title: { fr: 'Identité vérifiée et IA pour aéroports et transport | BCITI | ATNA', en: 'Verified identity & AI for airports and transportation | BCITI | ATNA' },
    description: {
      fr: 'Relier voyageurs, employés, partenaires et opérations aéroportuaires autour d’une identité vérifiée et d’un contexte partagé.',
      en: 'Connecting travellers, employees, partners and airport operations around a verified identity and shared context.',
    },
    kind: 'page',
    parent: '/secteurs',
    related: ['/platform', '/core-infrastructure', '/atna'],
  },
  {
    path: '/industries/health',
    name: { fr: 'Santé', en: 'Health' },
    title: { fr: 'Identité, consentement et accès pour réseaux de santé | BCITI | ATNA', en: 'Identity, consent & access for health networks | BCITI | ATNA' },
    description: {
      fr: 'Identité vérifiée, gestion des consentements et accès aux services pour les réseaux de santé, avec une IA qui vérifie avant d’agir.',
      en: 'Verified identity, consent management and access to services for health networks, with AI that verifies before it acts.',
    },
    kind: 'page',
    parent: '/secteurs',
    related: ['/core-infrastructure', '/trust', '/atna'],
  },
  {
    path: '/industries/telecom',
    name: { fr: 'Télécommunications et médias', en: 'Telecommunications & media' },
    title: { fr: 'Identité et relation abonnés pour télécoms et médias | BCITI | ATNA', en: 'Subscriber identity & engagement for telecom and media | BCITI | ATNA' },
    description: {
      fr: 'Transformer des abonnés anonymes en relations durables : identité vérifiée, consentements, communications et services personnalisés.',
      en: 'Turning anonymous subscribers into lasting relationships: verified identity, consent, communications and personalized services.',
    },
    kind: 'page',
    parent: '/secteurs',
    related: ['/core-infrastructure', '/communications', '/atna'],
  },
  {
    path: '/industries/universities',
    name: { fr: 'Universités et campus', en: 'Universities & campuses' },
    title: { fr: 'Carte étudiante numérique et plateforme campus | BCITI | ATNA', en: 'Digital student ID & campus platform | BCITI | ATNA' },
    description: {
      fr: 'Un campus est une mini-ville : identité vérifiée, carte numérique, accès, services et communications pour étudiants et personnel.',
      en: 'A campus is a small city: verified identity, digital card, access, services and communications for students and staff.',
    },
    kind: 'page',
    parent: '/secteurs',
    related: ['/digital-card', '/core-infrastructure', '/atna'],
  },
  {
    path: '/request-demo',
    name: { fr: 'Demander une démo', en: 'Request a demo' },
    title: { fr: 'Demander une démo | BCITI | ATNA', en: 'Request a demo | BCITI | ATNA' },
    description: {
      fr: 'Parlez-nous de votre organisation et voyons ensemble ce que BCITI | ATNA pourrait simplifier.',
      en: 'Tell us about your organization and let’s see together what BCITI | ATNA could simplify.',
    },
    kind: 'contact',
  },
]

/** Reachable but intentionally kept out of the index (legacy or placeholder pages). */
export const noindexPaths = [
  '/demo',
  // Duplicate of /industries/cities-government
  '/cities-government',
]

export function findSeoRoute(pathname: string): SeoRoute | undefined {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return seoRoutes.find((r) => r.path === clean)
}

export function pageUrl(path: string, lang: 'FR' | 'EN'): string {
  return `${SITE_URL}${path === '/' ? '/' : path}${lang === 'EN' ? '?lang=en' : ''}`
}
